import { apiClient } from "@/lib/api/axios";
import type { AuthProvider, AuthUser } from "../stores/authStore";

type Provider = "google" | "microsoft" | "apple";

interface BackendUserDto {
  id: string;
  email: string;
  name: string;
  brokerId: string;
  organizationId?: string;
  scopes: string[];
  profileCompleted: boolean;
  termsAccepted: boolean;
  emailVerified: boolean;
}

interface BackendLoginResponse {
  accessToken: string;
  expiresIn: number;
  user: BackendUserDto;
}

export interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

function mapUser(dto: BackendUserDto, authProvider: AuthProvider): AuthUser {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    brokerId: dto.brokerId,
    organizationId: dto.organizationId,
    role: "agent",
    authProvider,
    profileCompleted: dto.profileCompleted,
    termsAccepted: dto.termsAccepted,
    emailVerified: dto.emailVerified,
  };
}

export async function loginWithCredentials(input: {
  email: string;
  password: string;
  turnstileToken?: string;
}): Promise<AuthResponse> {
  const { turnstileToken, ...credentials } = input;
  // Token do Turnstile (anti-abuso, hub#345) vai no header, não no corpo.
  const { data } = await apiClient.post<BackendLoginResponse>("/auth/login", credentials, {
    headers: turnstileToken ? { "CF-Turnstile-Response": turnstileToken } : undefined,
  });
  return { accessToken: data.accessToken, user: mapUser(data.user, "local") };
}

export async function loginWithProvider(
  provider: Provider,
  payload: { idToken?: string; code?: string; redirectUri?: string; codeVerifier?: string; inviteToken?: string } = {},
): Promise<AuthResponse> {
  const { data } = await apiClient.post<BackendLoginResponse>(
    `/auth/oauth/${provider}/callback`,
    payload,
  );
  return { accessToken: data.accessToken, user: mapUser(data.user, provider) };
}

/** Prévia pública do convite de corretor (hub#489): nome de quem convidou, e-mail (travado) e status. */
export async function getPublicBrokerInvite(token: string): Promise<PublicBrokerInvite> {
  const { data } = await apiClient.get<PublicBrokerInvite>(`/public/broker-invites/${token}`);
  return data;
}

export interface PublicBrokerInvite {
  inviterName: string;
  email: string;
  status: "pending" | "accepted" | "expired" | "revoked";
}

// No body — refresh token is sent automatically as HttpOnly cookie
export async function refresh(): Promise<AuthResponse> {
  const { data } = await apiClient.post<BackendLoginResponse>("/auth/refresh");
  return { accessToken: data.accessToken, user: mapUser(data.user, "local") };
}

export async function requestPasswordReset(email: string, turnstileToken?: string) {
  const { data } = await apiClient.post<{ ok: boolean }>("/auth/forgot-password", { email }, {
    headers: turnstileToken ? { "CF-Turnstile-Response": turnstileToken } : undefined,
  });
  return data;
}

// Ativação de conta convidada pelo backoffice: valida o token do link, define a
// senha e já devolve a sessão (auto-login). Mesmo shape do login.
export async function activateAccount(token: string, password: string): Promise<AuthResponse> {
  const { data } = await apiClient.post<BackendLoginResponse>("/auth/activate-account", {
    token,
    password,
  });
  return { accessToken: data.accessToken, user: mapUser(data.user, "local") };
}

export async function resetPassword(token: string, newPassword: string, turnstileToken?: string) {
  const { data } = await apiClient.post<{ ok: boolean }>("/auth/reset-password", {
    token,
    newPassword,
  }, {
    headers: turnstileToken ? { "CF-Turnstile-Response": turnstileToken } : undefined,
  });
  return data;
}

// Confirma o e-mail a partir do token do link (anônimo; o token é o segredo). hub#345.
export async function verifyEmail(token: string) {
  const { data } = await apiClient.post<{ ok: boolean }>("/auth/verify-email", { token });
  return data;
}

// Reenvia o e-mail de verificação para o usuário logado (soft gate). hub#345.
export async function resendVerificationEmail() {
  const { data } = await apiClient.post<{ ok: boolean }>("/auth/resend-verification-email");
  return data;
}

// No body — refresh token read from cookie; backend also clears the cookie
export async function logout() {
  await apiClient.post("/auth/logout");
}
